import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ZOOD_MASCOT } from "@/lib/brand";
import {
  Sparkles, ArrowRight, GraduationCap, Handshake, DollarSign, Users, Clock,
  Zap, Star, ShieldCheck, TrendingUp, CheckCircle2, Ticket, Calendar,
  BookOpen, HeartHandshake, Rocket, PiggyBank,
} from "lucide-react";
import { PublicNavMobile } from "@/components/PublicNavMobile";

/**
 * Public marketing page: two earning paths on ZoodLeap — Tutoring Coach and
 * Reseller Partner. Kept in the ZoodLeap brand voice (teal accent, mascot,
 * playful copy) mirroring `/pages/Landing.jsx` so it looks like the same
 * product suite. Earnings disclaimer footer required by product.
 */
export default function EarnWithZoodLeap() {
  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1F2937]" data-testid="earn-page">
      <Nav />
      <Hero />
      <PathsGrid />
      <TutoringDeepDive />
      <ResellerDeepDive />
      <IncomeExamples />
      <WhyZoodLeap />
      <FAQ />
      <ClosingCTA />
      <Disclaimer />
      <Footer />
    </div>
  );
}

function Nav() {
  const linkClass = "block rounded-xl px-3 py-3 font-heading text-base font-semibold text-[#1F2937] hover:bg-[#F2ECE1]";
  const items = [
    { key: "#tutoring", label: "Tutoring Coach",  node: <a href="#tutoring"  className={linkClass}>Tutoring Coach</a> },
    { key: "#reseller", label: "Reseller Partner",node: <a href="#reseller"  className={linkClass}>Reseller Partner</a> },
    { key: "#examples", label: "Income Examples", node: <a href="#examples"  className={linkClass}>Income Examples</a> },
    { key: "#faq",      label: "FAQ",             node: <a href="#faq"       className={linkClass}>FAQ</a> },
    { key: "/",         label: "Home",            node: <Link to="/"         className={linkClass}>Home</Link> },
    { key: "/login",    label: "Log In",          node: <Link to="/login"    className={linkClass}>Log In</Link> },
  ];
  return (
    <header className="sticky top-0 z-40 border-b border-[#E5DECF] bg-[#FAF7F2]/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/brand/logo.png" alt="ZoodLeap" className="h-9 w-9 rounded-2xl" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="font-heading text-lg font-extrabold text-[#1F2937]">ZoodLeap</div>
        </Link>
        <nav className="hidden items-center gap-1 md:flex">
          <a href="#tutoring" className="rounded-full px-4 py-2 text-sm font-semibold text-[#1F2937] hover:bg-[#F2ECE1]">Tutoring Coach</a>
          <a href="#reseller" className="rounded-full px-4 py-2 text-sm font-semibold text-[#1F2937] hover:bg-[#F2ECE1]">Reseller Partner</a>
          <a href="#examples" className="rounded-full px-4 py-2 text-sm font-semibold text-[#1F2937] hover:bg-[#F2ECE1]">Income Examples</a>
          <a href="#faq" className="rounded-full px-4 py-2 text-sm font-semibold text-[#1F2937] hover:bg-[#F2ECE1]">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/signup?partner=1" data-testid="nav-partner-cta"
            className="hidden rounded-full bg-[#0D9488] px-4 py-2 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#0B7F74] active:translate-y-0.5 md:inline-flex">
            Start Earning
          </Link>
          <PublicNavMobile
            items={items}
            cta={
              <Link
                to="/signup?partner=1"
                data-testid="nav-partner-cta-mobile"
                className="block rounded-2xl bg-[#0D9488] px-5 py-3 text-center font-heading text-base font-bold text-white shadow-sm"
              >
                Start Earning
              </Link>
            }
          />
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-20 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="inline-flex items-center gap-2 rounded-full bg-[#FF9E1B]/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#B45309]">
          <PiggyBank size={14} /> Earn income with ZoodLeap
        </div>
        <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
          Turn what you already do <span className="text-[#0D9488]">into a business</span>.
        </h1>
        <p className="mt-5 font-body text-base sm:text-lg text-[#6B7280] max-w-xl">
          Whether you tutor one family at a time or want to distribute a full curriculum to hundreds — ZoodLeap gives you two
          plug-and-play ways to earn. Bring the students, we bring the platform.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#tutoring" data-testid="hero-tutor-cta"
            className="inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-base font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5">
            <GraduationCap size={18} /> Tutor with ZoodLeap
          </a>
          <a href="#reseller" data-testid="hero-reseller-cta"
            className="inline-flex items-center gap-2 rounded-2xl bg-white border-2 border-[#E5DECF] px-6 py-4 text-base font-bold text-[#1F2937] transition-all hover:border-[#0D9488] active:translate-y-0.5">
            <Handshake size={18} /> Become a reseller
          </a>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-4 text-xs font-body text-[#6B7280]">
          <span className="inline-flex items-center gap-1"><ShieldCheck size={14} className="text-[#10B981]" /> No approval needed to start</span>
          <span className="inline-flex items-center gap-1"><Clock size={14} className="text-[#0D9488]" /> Set up in under 10 minutes</span>
          <span className="inline-flex items-center gap-1"><DollarSign size={14} className="text-[#FF9E1B]" /> Keep 100% of what you charge</span>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
        className="relative flex items-center justify-center">
        <div className="absolute inset-0 -z-0 mx-auto h-[85%] w-[85%] self-center rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(13,148,136,0.20) 0%, rgba(122,63,242,0.10) 60%, transparent 100%)" }} />
        <motion.img
          src={ZOOD_MASCOT} alt="Zoodle mascot" data-testid="hero-mascot"
          className="relative z-10 w-[70%] max-w-[400px] drop-shadow-[0_20px_40px_rgba(23,190,187,0.25)]"
          animate={{ y: [0, -14, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
        {/* Floating income cards */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}
          className="absolute -bottom-2 -left-2 z-20 hidden rounded-2xl border border-[#E5DECF] bg-white px-4 py-3 shadow-lg sm:flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#10B981] text-white"><DollarSign size={18} /></div>
          <div className="leading-tight">
            <div className="font-heading text-sm font-bold">+$1,247 this month</div>
            <div className="font-body text-xs text-[#6B7280]">Reseller earnings</div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 }}
          className="absolute -top-1 right-0 z-20 hidden rounded-2xl border border-[#E5DECF] bg-white px-4 py-3 shadow-lg sm:flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#7A3FF2] text-white"><GraduationCap size={18} /></div>
          <div className="leading-tight">
            <div className="font-heading text-sm font-bold">6 new students</div>
            <div className="font-body text-xs text-[#6B7280]">Booked this week</div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

function PathsGrid() {
  return (
    <section id="paths" className="mx-auto max-w-7xl px-4 pb-6 sm:px-6 lg:px-8">
      <div className="text-center">
        <div className="text-xs uppercase tracking-widest font-bold text-[#0D9488]">Two paths to earn</div>
        <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Pick the one that fits your day</h2>
      </div>
      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <PathCard
          testid="path-tutor"
          icon={GraduationCap}
          tint="#0D9488"
          badge="Path 1"
          title="Tutoring Coach"
          tagline="Charge families for your time. Use ZoodLeap as your classroom."
          bullets={[
            "Bring your own students — one family at a time",
            "Set your own hourly rate, keep 100%",
            "Assign lessons, run live sessions, track progress",
            "No inventory, no lesson prep — the curriculum is done",
          ]}
          bestFor="Teachers, retired educators, home-school parents, college students who love kids."
          href="#tutoring"
          cta="See how tutoring works"
        />
        <PathCard
          testid="path-reseller"
          icon={Handshake}
          tint="#7A3FF2"
          badge="Path 2"
          title="Reseller Partner"
          tagline="Bundle ZoodLeap as your own product. Charge whatever you'd like."
          bullets={[
            "Get a white-label branded page (yourdomain.com/r/yourbrand)",
            "Sell subscription plans with your own Stripe — keep 100%",
            "Starts at 10 seats for $1,500/year — scale up as you grow",
            "License codes for offline enrollment (schools, districts)",
          ]}
          bestFor="Tutoring companies, home-school co-ops, content creators, private academies."
          href="#reseller"
          cta="See how reselling works"
        />
      </div>
    </section>
  );
}

function PathCard({ testid, icon: Icon, tint, badge, title, tagline, bullets, bestFor, href, cta }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      data-testid={testid}
      className="rounded-3xl border-2 bg-white p-6 sm:p-8" style={{ borderColor: `${tint}33` }}>
      <div className="flex items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ background: tint }}><Icon size={28} /></div>
        <div>
          <div className="font-body text-[10px] uppercase tracking-widest font-bold" style={{ color: tint }}>{badge}</div>
          <h3 className="font-heading text-2xl font-extrabold text-[#1F2937]">{title}</h3>
        </div>
      </div>
      <p className="mt-3 font-body text-[15px] text-[#1F2937]">{tagline}</p>
      <ul className="mt-4 space-y-2">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 font-body text-sm text-[#1F2937]">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: tint }} /> {b}
          </li>
        ))}
      </ul>
      <div className="mt-5 rounded-2xl bg-[#F2ECE1]/50 p-3 font-body text-xs text-[#6B7280]">
        <b className="text-[#1F2937]">Best for:</b> {bestFor}
      </div>
      <a href={href} className="mt-5 inline-flex items-center gap-2 font-heading text-sm font-bold hover:underline" style={{ color: tint }}>
        {cta} <ArrowRight size={16} />
      </a>
    </motion.div>
  );
}

function TutoringDeepDive() {
  const steps = [
    { icon: Users, title: "Bring your students", text: "Post-and-share on Facebook groups, Nextdoor, or word-of-mouth. Parents pay you directly through your usual Zelle/CashApp/invoice." },
    { icon: BookOpen, title: "Assign ZoodLeap lessons", text: "Log in as a Teacher, add the child, pick their grade, and drop in a cursive plan, reading pack, or math playlist in seconds." },
    { icon: Clock, title: "Run your sessions", text: "Meet online or in-person. The kid does the interactive lessons, you coach and answer questions." },
    { icon: TrendingUp, title: "Show real progress", text: "Weekly reports, attempt history and printable certificates prove your value — and keep families paying." },
  ];
  return (
    <section id="tutoring" className="bg-white py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#0D9488]">Path 1 · Tutoring Coach</div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Tutor with ZoodLeap</h2>
            <p className="mt-3 font-body text-[#6B7280]">
              A ready-made curriculum + progress tracking + printable certificates means you spend your time <b>teaching</b> —
              not lesson-planning. Charge what you want, keep 100% of what parents pay you.
            </p>
            <div className="mt-6 rounded-3xl border-2 border-[#0D9488]/25 bg-[#0D9488]/5 p-5">
              <div className="font-heading text-lg font-extrabold text-[#0D9488]">Quick math</div>
              <p className="mt-1 font-body text-sm text-[#1F2937]">
                Tutor <b>6 kids</b> for 45 minutes twice a week at <b>$40/session</b>.
                <br />That's <b>6 × 2 × $40 = $480/week</b> or roughly <b>$1,920/mo</b> for ~9 hours of teaching time each week.
              </p>
              <div className="mt-2 font-body text-[11px] text-[#6B7280]">Illustrative only — see full disclaimer at bottom.</div>
            </div>
            <Link to="/signup" data-testid="tutor-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 font-heading text-base font-bold text-white shadow-md hover:bg-[#0B7F74] active:translate-y-0.5">
              <GraduationCap size={18} /> Sign up as a teacher
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {steps.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="rounded-3xl border border-[#E5DECF] bg-[#FAF7F2] p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#0D9488]/12 text-[#0D9488]"><s.icon size={20} /></div>
                <div className="mt-3 font-heading font-extrabold">{s.title}</div>
                <p className="mt-1 font-body text-sm text-[#6B7280]">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ResellerDeepDive() {
  const steps = [
    { icon: Handshake, title: "Pick your tier", text: "Starts at 10 seats for $1,500/year. Scale up with Growth (25 seats) or Partner+ (50 seats) as you grow. Every seat = one parent/teacher account you can onboard." },
    { icon: Sparkles, title: "Set your brand", text: "Upload your logo, pick an accent color, choose your slug. Your public landing is at yourdomain.com/r/yourbrand." },
    { icon: Ticket, title: "Sell how you like", text: "Recurring plans (Stripe checkout on your own account) or license codes for offline schools and districts." },
    { icon: DollarSign, title: "Get paid directly", text: "Stripe Connect routes every payment to your Stripe account. ZoodLeap collects 0% platform fee." },
  ];
  return (
    <section id="reseller" className="bg-[#1F2937] py-16 text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div className="grid gap-4 sm:grid-cols-2">
            {steps.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="rounded-3xl bg-white/5 p-5 ring-1 ring-white/10">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#7A3FF2] text-white"><s.icon size={20} /></div>
                <div className="mt-3 font-heading font-extrabold">{s.title}</div>
                <p className="mt-1 font-body text-sm text-white/70">{s.text}</p>
              </motion.div>
            ))}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">Path 2 · Reseller Partner</div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Bundle ZoodLeap as your own product</h2>
            <p className="mt-3 font-body text-white/80">
              White-label everything — logo, colors, plan pricing. Families sign up on your branded landing page, pay you directly,
              and never see the ZoodLeap billing brand.
            </p>
            <div className="mt-6 rounded-3xl bg-white/5 p-5 ring-1 ring-white/10">
              <div className="font-heading text-lg font-extrabold text-[#7A3FF2]">Quick math</div>
              <p className="mt-1 font-body text-sm text-white/90">
                Reseller tiers <b>start at 10 seats for $1,500/year</b>. Fill all 10 with Family plans at <b>$19.99/mo</b>
                and you're at <b>$199.90/mo</b> or ~<b>$2,398/yr</b> — profit ~<b>$898/yr</b> after your platform fee, all going straight to your Stripe.
                <br />Ready to scale? Growth (25 seats · $2,500/yr) and Partner+ (50 seats · $3,999/yr) unlock deeper margins.
              </p>
              <div className="mt-2 font-body text-[11px] text-white/50">Illustrative only — see full disclaimer at bottom.</div>
            </div>
            <Link to="/signup?partner=1" data-testid="reseller-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-[#7A3FF2] px-6 py-4 font-heading text-base font-bold text-white shadow-md hover:brightness-95 active:translate-y-0.5">
              <Handshake size={18} /> Become a reseller partner
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function IncomeExamples() {
  const rows = [
    { path: "Tutor · Part-time", detail: "4 kids · 2 sessions/wk · $35/session", weekly: "$280", monthly: "$1,120", tone: "#0D9488" },
    { path: "Tutor · Full-time", detail: "10 kids · 3 sessions/wk · $45/session", weekly: "$1,350", monthly: "$5,400", tone: "#0D9488" },
    { path: "Reseller · Starter (10 seats)", detail: "10 Family plans @ $19.99/mo · $1,500/yr platform fee", weekly: "—", monthly: "$199.90", tone: "#7A3FF2" },
    { path: "Reseller · Growth (25 seats)", detail: "25 Family plans @ $19.99/mo · $2,500/yr platform fee", weekly: "—", monthly: "$499.75", tone: "#7A3FF2" },
    { path: "Reseller · Partner+ (50 seats)", detail: "50 mixed plans, blended $29/mo · $3,999/yr platform fee", weekly: "—", monthly: "$1,450", tone: "#7A3FF2" },
  ];
  return (
    <section id="examples" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="text-center">
        <div className="text-xs uppercase tracking-widest font-bold text-[#FF9E1B]">Income scenarios</div>
        <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">What your day could look like</h2>
        <p className="mx-auto mt-3 max-w-2xl font-body text-sm text-[#6B7280]">
          These are examples — not guarantees. Your results depend entirely on the students you bring, prices you set, and effort you put in.
        </p>
      </div>
      <div className="mt-10 overflow-x-auto rounded-3xl border border-[#E5DECF] bg-white">
        <table className="w-full min-w-[640px] text-left">
          <thead className="bg-[#F2ECE1]/50 font-body text-xs uppercase tracking-wide text-[#6B7280]">
            <tr>
              <th className="px-5 py-3 font-bold">Path</th>
              <th className="px-5 py-3 font-bold">Setup</th>
              <th className="px-5 py-3 font-bold">Weekly</th>
              <th className="px-5 py-3 font-bold">Monthly</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5DECF] font-body text-sm">
            {rows.map((r) => (
              <tr key={r.path} data-testid={`example-${r.path.toLowerCase().replace(/\W+/g, "-")}`}>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ background: r.tone }} />
                    <span className="font-heading font-extrabold text-[#1F2937]">{r.path}</span>
                  </div>
                </td>
                <td className="px-5 py-4 text-[#6B7280]">{r.detail}</td>
                <td className="px-5 py-4 font-heading font-bold text-[#1F2937]">{r.weekly}</td>
                <td className="px-5 py-4 font-heading font-bold" style={{ color: r.tone }}>{r.monthly}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function WhyZoodLeap() {
  const cards = [
    { icon: BookOpen, title: "Full PreK–8th curriculum", text: "Reading, phonics, vocabulary, cursive, math, science, foreign language. Nothing to build." },
    { icon: HeartHandshake, title: "Kids actually stick", text: "Points, stickers, badges and printable certificates keep them showing up — which keeps parents paying." },
    { icon: Zap, title: "Setup in 10 minutes", text: "Sign up, add students (or codes), share your link. No approval process, no waiting." },
    { icon: ShieldCheck, title: "Privacy-first", text: "COPPA & FERPA-aligned. No ads, no trackers. Parents trust the platform you're delivering." },
  ];
  return (
    <section className="bg-[#F2ECE1]/50 py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest font-bold text-[#0D9488]">Why ZoodLeap</div>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Everything you need to sell learning</h2>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="rounded-3xl border border-[#E5DECF] bg-white p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0D9488]/12 text-[#0D9488]"><c.icon size={22} /></div>
              <div className="mt-3 font-heading font-bold text-[#1F2937]">{c.title}</div>
              <p className="mt-1 font-body text-sm text-[#6B7280]">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const qs = [
    { q: "Do I need teaching credentials to tutor?", a: "No — you're the coach, and the platform provides the lessons. That said, families will trust you more if you can share a background in education, home-schooling, or working with kids." },
    { q: "Do I need to charge families through ZoodLeap when tutoring?", a: "No. Tutoring transactions happen directly between you and the family (Zelle, CashApp, invoice, cash — however you like). ZoodLeap is your teaching tool, not your billing tool for tutoring." },
    { q: "How much does the reseller program cost?", a: "Reseller tiers start at 10 seats for $1,500/year — that's the entry point. You can scale up to Growth (100 seats) or Partner (300 seats) as your roster grows. All payments from families flow straight to your own Stripe account; ZoodLeap only collects your annual platform fee." },
    { q: "How much does ZoodLeap take from reseller sales?", a: "Zero. When you connect your Stripe account, every dollar a family pays for your plan lands in your Stripe. ZoodLeap makes its money from the reseller tier subscription you pay us — nothing else." },
    { q: "Can I switch between paths later?", a: "Yes. Many resellers start as tutors, prove the model with a handful of families, and upgrade to a reseller tier once they have enough demand to bundle plans." },
    { q: "What if I don't want to charge for tutoring — just use ZoodLeap for my own kids?", a: "You can! A free ZoodLeap account gives you access to a sample lesson per subject. Full curriculum access unlocks with any paid plan." },
    { q: "Can I offer 1-on-1 tutoring AND sell reseller plans at the same time?", a: "Absolutely. You'd sign up as a reseller partner and continue tutoring your own students on the side. Some of your best plan buyers may be families that already trust you as a tutor." },
  ];
  return (
    <section id="faq" className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="text-center">
        <div className="text-xs uppercase tracking-widest font-bold text-[#0D9488]">Common questions</div>
        <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Frequently asked</h2>
      </div>
      <div className="mt-8 space-y-3">
        {qs.map((row, i) => (
          <details key={i} data-testid={`faq-${i}`}
            className="group rounded-3xl border border-[#E5DECF] bg-white p-5 open:bg-[#FAF7F2]">
            <summary className="flex cursor-pointer list-none items-center justify-between font-heading text-base font-bold text-[#1F2937]">
              {row.q}
              <span className="ml-4 text-[#0D9488] transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="mt-3 font-body text-sm text-[#6B7280]">{row.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
      <div className="rounded-[32px] p-8 sm:p-12 text-center text-white"
        style={{ background: "linear-gradient(135deg, #0D9488 0%, #7A3FF2 100%)" }}>
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Ready to make ZoodLeap your side (or main) hustle?</h2>
        <p className="mt-2 font-body text-white/85">Free to sign up. Upgrade only when you're ready to earn.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to="/signup" data-testid="closing-tutor-cta"
            className="inline-flex items-center gap-2 rounded-2xl bg-white px-6 py-4 font-heading font-bold text-[#0D9488] shadow-md transition-all hover:brightness-95 active:translate-y-0.5">
            <GraduationCap size={18} /> Start tutoring free
          </Link>
          <Link to="/signup?partner=1" data-testid="closing-reseller-cta"
            className="inline-flex items-center gap-2 rounded-2xl bg-[#7A3FF2] px-6 py-4 font-heading font-bold text-white ring-1 ring-white/30 transition-all hover:brightness-95 active:translate-y-0.5">
            <Handshake size={18} /> Become a reseller
          </Link>
        </div>
      </div>
    </section>
  );
}

function Disclaimer() {
  return (
    <section className="border-t border-[#E5DECF] bg-[#F2ECE1]/40 py-10">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-start gap-3 rounded-2xl border-2 border-[#F59E0B]/40 bg-white p-5" data-testid="earnings-disclaimer">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-[#F59E0B]/15 text-[#B45309]">
            <ShieldCheck size={18} />
          </div>
          <div>
            <div className="font-heading text-sm font-extrabold uppercase tracking-widest text-[#B45309]">Earnings Disclaimer</div>
            <p className="mt-1 font-body text-xs text-[#78350F] leading-relaxed">
              The income scenarios and dollar figures shown on this page are <b>illustrative examples only</b>, provided
              to help you think through the model. They are <b>not guarantees, promises, or forecasts</b> of any specific
              level of earnings you will achieve as a ZoodLeap tutoring coach or reseller partner. Your actual results
              depend on many factors outside of ZoodLeap's control — including how many students or families you attract,
              the prices you set, the market you serve, your marketing effort, your reliability, applicable local taxes
              and business regulations, and general economic conditions. Some people will earn more than the examples
              shown; many will earn less; some will earn nothing at all. ZoodLeap makes <b>no representation or warranty</b> that
              any specific income can or will be achieved by any participant. By choosing to tutor or resell with
              ZoodLeap, you accept full responsibility for your own business decisions, tax reporting, and outcomes.
              Always do your own research and consider consulting a qualified financial or business advisor before
              relying on any earnings estimate.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#1F2937] py-10 text-center">
      <div className="mx-auto max-w-7xl px-4">
        <div className="font-heading text-lg font-extrabold text-white">ZoodLeap</div>
        <p className="mt-2 font-body text-sm text-white/60">The complete PreK–8th interactive learning platform — earn on your terms.</p>
        <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs text-white/60">
          <Link to="/" className="hover:text-white">Home</Link>
          <a href="#tutoring" className="hover:text-white">Tutor</a>
          <a href="#reseller" className="hover:text-white">Reseller</a>
          <a href="#faq" className="hover:text-white">FAQ</a>
          <Link to="/signup" className="hover:text-white">Sign up</Link>
        </div>
        <p className="mt-4 font-body text-[11px] text-white/40">© {new Date().getFullYear()} ZoodLeap · Privacy-first · COPPA &amp; FERPA aligned</p>
      </div>
    </footer>
  );
}
